/**
 * Quick selection for the SearchPage — the one chip row above the results.
 *
 * **Raumtyp** → `publicData.categoryLevel1`, single select: Alle · Arbeitsplatz · Meetingraum ·
 * Büro. The three groups are the app's one taxonomy (components/CategoryShortcuts/categoryGroups.js,
 * shared with the homepage shortcuts and the city pages), so a chip writes exactly the
 * comma-separated id list those links write — and the city page and /s offer the same four chips.
 *
 * The row used to carry the rental length (Stündlich / Tagespass / Monatlich / Individuell). The
 * length moved behind "Filter": a visitor first decides what kind of room, the booking model is a
 * refinement — and the city pages, the Ads final URLs, already ask the space-type question at the
 * top. One question, one place, the same on both pages (.claude/skills/apple-hig-web, §1).
 *
 * The rental-length helpers stay: the search facet (SearchPage.shared resolveSearchFacet), the AI
 * concierge and the iOS mirror read them. Lengths are matched against the hosted listing types,
 * case-insensitively (cowork24's listing type ids mix casing, e.g. `Lounge`).
 */

import { LOUNGE_MATCHER } from './airportLoungeConfig';
import { resolveGroups } from '../components/CategoryShortcuts/categoryGroups';

/**
 * The rental lengths, in ascending commitment. `listingTypeIds` are the hosted
 * listing type ids (lowercased) that belong to the length — the same mapping the
 * iOS `RentalDuration.resolve` uses. Airport lounges are deliberately absent:
 * they're one-off partner bookings, not a rental by length.
 *
 * `flatRate` keeps the iOS key but is labelled "Individuell", not "Pauschal": the
 * listing type behind it is `individual` / unitType `fixed`, i.e. a period the HOST
 * defines and prices as one slot (Halbtag, Abend, Workshop-Fenster) — the Console
 * calls it "Individueller Zeitraum". "Pauschal" described the price, which is not
 * the question this row answers.
 */
const RENTAL_DURATIONS = [
  { key: 'hourly', listingTypeIds: ['hourly-rental'], unitTypes: ['hour'] },
  { key: 'day', listingTypeIds: ['tagespass'], unitTypes: ['day', 'night'] },
  // Two ids, one length: `fixdesk` is the purchase-process monthly desk, `cowork24-abo`
  // the membership that is meant to replace it. The Abo type only exists where
  // REACT_APP_ABO_ENABLED is set, and availableRentalDurations emits only the ids the
  // marketplace actually configures — so this is a no-op wherever the Abo is off.
  { key: 'monthly', listingTypeIds: ['fixdesk', 'cowork24-abo'], unitTypes: ['week', 'month'] },
  { key: 'flatRate', listingTypeIds: ['individual'], unitTypes: ['fixed'] },
];

/**
 * Unit of a merged listing type, whichever shape it came in: the hosted asset puts
 * `unitType` at the top level, `configListing.js` nests it under `transactionType`.
 */
const unitTypeOf = listingType =>
  `${listingType?.transactionType?.unitType || listingType?.unitType || ''}`.toLowerCase();

/** The URL parameter the length row writes. */
export const LISTING_TYPE_PARAM = 'pub_listingType';

/** True when some length already claims this listing type by id. */
const isMappedById = listingType =>
  RENTAL_DURATIONS.some(d =>
    d.listingTypeIds.includes(`${listingType?.listingType}`.toLowerCase())
  );

/**
 * The lengths this marketplace can actually offer, with the hosted listing type
 * ids resolved in their original casing (that's what the API expects back).
 * A length whose listing type doesn't exist is dropped — a filter that can only
 * ever return nothing is a dead end.
 *
 * Resolution order mirrors the iOS `RentalDuration.resolve`: the listing type id
 * first, its unit only as a fallback, so a type added in Console still lands in the
 * right length instead of falling outside every one of them.
 *
 * Unlike iOS, `item` is NOT a fallback unit. iOS only ever sees cowork24 listings,
 * where `item` means the monthly fixdesk; here it is the template's unit for anything
 * sold as a thing — a product listing would turn into "Monatlich". `fixdesk` and the
 * lounges are matched by id, so nothing is lost. The lounges are dropped up front
 * regardless, since they are partner bookings rather than a rental by length.
 *
 * @param {Array<Object>} listingTypes `config.listing.listingTypes`
 * @returns {Array<{key: string, listingTypeIds: Array<string>}>}
 */
export const availableRentalDurations = (listingTypes = []) => {
  const rentable = listingTypes.filter(
    lt => lt?.listingType && !LOUNGE_MATCHER.test(lt.listingType)
  );

  return RENTAL_DURATIONS.map(duration => {
    const byId = rentable.filter(lt =>
      duration.listingTypeIds.includes(`${lt.listingType}`.toLowerCase())
    );
    const matchedIds = byId.map(lt => lt.listingType);
    const byUnit = rentable.filter(
      lt =>
        !matchedIds.includes(lt.listingType) &&
        !isMappedById(lt) &&
        duration.unitTypes.includes(unitTypeOf(lt))
    );
    const ids = [...matchedIds, ...byUnit.map(lt => lt.listingType)];

    // `unitTypes` stays internal: callers only ever need the key and the ids to query.
    return ids.length > 0 ? { key: duration.key, listingTypeIds: ids } : null;
  }).filter(Boolean);
};

/**
 * @param {string|Array<string>} value raw `pub_listingType` value from the URL
 * @returns {Array<string>} the selected listing type ids
 */
export const parseListingTypeParam = value =>
  `${value ?? ''}`
    .split(',')
    .map(id => id.trim())
    .filter(Boolean);

/**
 * @param {Object} duration entry from `availableRentalDurations`
 * @param {Array<string>} selectedIds currently selected listing type ids
 * @returns {boolean}
 */
export const isDurationSelected = (duration, selectedIds = []) =>
  duration.listingTypeIds.some(id => selectedIds.includes(id));

/**
 * Toggles one length in the selection.
 *
 * @param {Object} duration entry from `availableRentalDurations`
 * @param {Array<string>} selectedIds currently selected listing type ids
 * @returns {string|null} the new `pub_listingType` value, or null when nothing is left
 */
export const toggleRentalDuration = (duration, selectedIds = []) => {
  const next = isDurationSelected(duration, selectedIds)
    ? selectedIds.filter(id => !duration.listingTypeIds.includes(id))
    : [...selectedIds, ...duration.listingTypeIds.filter(id => !selectedIds.includes(id))];

  return next.length > 0 ? next.join(',') : null;
};

/**
 * The space-type groups this marketplace can offer, resolved against the hosted categories. A
 * group none of whose ids exists is dropped (resolveGroups) — a chip that can only return
 * nothing is a dead end.
 *
 * @param {Array<Object>} listingCategories hosted category tree (top level)
 * @returns {Array<{key: string, ids: Array<string>, labelId: string, Icon: Function}>}
 */
export const availableSpaceTypes = (listingCategories = []) => resolveGroups(listingCategories);

/**
 * Which group the current category param selects, or null for "all" and for anything that is not
 * exactly one group (a lounge search, a single sub-type picked in the panel).
 *
 * @param {Array} groups from availableSpaceTypes
 * @param {string|Array<string>} value raw `pub_categoryLevel1` value from the URL
 * @returns {string|null} group key
 */
export const selectedSpaceType = (groups = [], value) => {
  const ids = parseListingTypeParam(value);
  if (ids.length === 0) {
    return null;
  }
  const group = groups.find(g => ids.every(id => g.ids.includes(id)));
  return group ? group.key : null;
};

/**
 * Keys of the built-in filters the quick row already owns, so the "Filter" panel can leave them
 * out instead of offering the same param twice.
 *
 * Mirrors the component's own render condition — a filter is only dropped when the chip row that
 * replaces it actually renders. A NESTED category tree keeps its panel filter: the chips only ever
 * set categoryLevel1, so hiding it would put the deeper levels out of reach.
 *
 * The length (`listingType`) is never claimed any more: it lives in the panel now.
 *
 * @param {Object} params
 * @param {Array<Object>} params.listingCategories hosted category tree (top level)
 * @returns {Array<string>} filter keys to omit ('categoryLevel' or nothing)
 */
export const quickFilteredFilterKeys = ({ listingCategories = [] } = {}) => {
  const isNested = listingCategories.some(c => c?.subcategories?.length > 0);
  return availableSpaceTypes(listingCategories).length > 0 && !isNested ? ['categoryLevel'] : [];
};
